import React, { useMemo, useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentDots } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { submitFeedback } from '../../../../services/FeedbackService';

const FEEDBACK_TYPES = [
    { value: 'general', label: 'Allmän feedback' },
    { value: 'bug', label: 'Fel / bugg' },
    { value: 'feature', label: 'Önskemål / ny funktion' },
    { value: 'other', label: 'Annat' },
];

const MAX_MESSAGE_LENGTH = 5000;

function collectMetadata() {
    return {
        appVersion: process.env.REACT_APP_BUILD_SHA || process.env.REACT_APP_BUILD_TAG || null,
        buildNumber: process.env.REACT_APP_BUILD_NUMBER || null,
        viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        language: typeof navigator !== 'undefined' ? navigator.language : null,
        submittedAt: new Date().toISOString(),
    };
}

const FeedbackWidget = ({ show, onHide, source = 'sidebar' }) => {
    const location = useLocation();
    const user = useSelector((state) => state.auth.user);
    const selectedCompanyId = useSelector((state) => state.auth.selectedCompanyId);

    const [type, setType] = useState('general');
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const contextSummary = useMemo(() => {
        const parts = [];
        if (location?.pathname) parts.push(location.pathname);
        if (source) parts.push(`källa: ${source}`);
        return parts.join('  ·  ');
    }, [location?.pathname, source]);

    const reset = () => {
        setType('general');
        setMessage('');
        setError(null);
        setSuccess(false);
        setSubmitting(false);
    };

    const handleClose = () => {
        if (submitting) return;
        reset();
        onHide?.();
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        const trimmed = message.trim();
        if (!trimmed) {
            setError('Skriv ett meddelande innan du skickar.');
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await submitFeedback({
                type,
                source,
                path: location?.pathname || null,
                message: trimmed,
                companyId: selectedCompanyId || null,
                email: user?.email || null,
                metadata: collectMetadata(),
            });
            setSuccess(true);
            setMessage('');
        } catch (err) {
            const serverMsg = err?.response?.data?.message || err?.message;
            setError(serverMsg || 'Kunde inte skicka feedback. Försök igen.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <FontAwesomeIcon icon={faCommentDots} className="me-2 text-primary" />
                    Skicka feedback
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {success ? (
                        <Alert variant="success" className="mb-0">
                            Tack! Din feedback har skickats. Vi läser allt och återkommer vid behov.
                        </Alert>
                    ) : (
                        <>
                            <p className="text-muted" style={{ fontSize: '0.9em' }}>
                                Berätta vad du tycker, vad som krånglar eller vad du saknar.
                                Vi får automatiskt med vilken sida du är på och lite teknisk info
                                för att kunna hjälpa bättre.
                            </p>

                            <Form.Group className="mb-3">
                                <Form.Label>Typ</Form.Label>
                                <Form.Select
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                    disabled={submitting}
                                >
                                    {FEEDBACK_TYPES.map((t) => (
                                        <option key={t.value} value={t.value}>
                                            {t.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Meddelande</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={6}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Beskriv så detaljerat du kan..."
                                    maxLength={MAX_MESSAGE_LENGTH}
                                    disabled={submitting}
                                    autoFocus
                                />
                                <Form.Text className="text-muted">
                                    {message.length} / {MAX_MESSAGE_LENGTH}
                                </Form.Text>
                            </Form.Group>

                            {contextSummary && (
                                <div
                                    style={{
                                        fontSize: '0.8em',
                                        color: '#888',
                                        background: '#f8f9fa',
                                        padding: '8px 12px',
                                        borderRadius: 4,
                                    }}
                                >
                                    Kontext som skickas med: {contextSummary}
                                </div>
                            )}

                            {error && (
                                <Alert variant="danger" className="mt-3 mb-0">
                                    {error}
                                </Alert>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                        {success ? 'Stäng' : 'Avbryt'}
                    </Button>
                    {!success && (
                        <Button variant="primary" type="submit" disabled={submitting || !message.trim()}>
                            {submitting ? 'Skickar...' : 'Skicka'}
                        </Button>
                    )}
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default FeedbackWidget;
